import { BaseQueryFn, createApi, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { fetchQueryWithToken, getSettings, SettingsState, transfromTranscoderErrors } from '..';
import { ETranscoderTaskKind, TranscoderEncoder, TranscoderTask, TranscoderWorker } from '../../models';
import { setRtmpValidPid, transformTaskRequest } from './transcoderLiveTask.helpers';

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const settings = getSettings(api.getState() as SettingsState);
  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = `${settings?.servers.serverTranscoderConfigurator ?? ''}/api/v1/${urlEnd}`;
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };
  return fetchQueryWithToken(adjustedArgs, api, extraOptions);
};

export const liveTaskTag = 'TranscoderLiveTask' as const;
export const workerTag = 'TranscoderWorker' as const;

export const transcoderLiveTaskApi = createApi({
  reducerPath: 'transcoderLiveTaskApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: [liveTaskTag, workerTag],
  endpoints: builder => ({
    getTranscoderLiveTasks: builder.query<TranscoderTask[], void>({
      query: () => `task?kind=${ETranscoderTaskKind.live}`,
      providesTags: [liveTaskTag],
      transformResponse: (response: TranscoderTask[]) => {
        for (const task of response) {
          setRtmpValidPid(task);

          if (task.template) {
            task.template.videoProfiles.forEach((profile, index) => {
              task.template.videoProfiles[index].resolution = `${profile.width}x${profile.height}`;
            });
          }
        }
        return response;
      },
    }),
    getTranscoderLiveTask: builder.query<TranscoderTask, string>({
      query: id => `task/${id}`,
    }),
    deleteTranscoderLiveTask: builder.mutation<TranscoderTask, string>({
      query: id => ({
        url: `task/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [liveTaskTag, workerTag],
    }),
    createTranscoderLiveTask: builder.mutation<TranscoderTask, TranscoderTask>({
      query: channel => ({
        url: 'task',
        method: 'POST',
        body: transformTaskRequest(channel),
      }),
      invalidatesTags: [liveTaskTag, workerTag],
      transformErrorResponse: (response: FetchBaseQueryError, meta, arg: TranscoderTask) =>
        transfromTranscoderErrors<TranscoderTask>(response, arg),
    }),
    updateTranscoderLiveTask: builder.mutation<TranscoderTask, TranscoderTask>({
      query: channel => ({
        url: 'task',
        method: 'PATCH',
        body: transformTaskRequest(channel),
      }),
      invalidatesTags: [liveTaskTag],
      transformErrorResponse: (response: FetchBaseQueryError, meta, arg: TranscoderTask) =>
        transfromTranscoderErrors<TranscoderTask>(response, arg),
    }),
    deleteTranscoderLiveTaskHolistic: builder.mutation<TranscoderTask, string>({
      query: id => ({
        url: `holistictask/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [liveTaskTag, workerTag],
    }),
    createTranscoderLiveTaskHolistic: builder.mutation<TranscoderTask, TranscoderTask>({
      query: task => ({
        url: 'holistictask',
        method: 'POST',
        body: task,
      }),
      invalidatesTags: [liveTaskTag, workerTag],
    }),
    updateTranscoderLiveTaskHolistic: builder.mutation<TranscoderTask, TranscoderTask>({
      query: task => ({
        url: 'holistictask',
        method: 'PATCH',
        body: task,
      }),
      invalidatesTags: [liveTaskTag],
    }),
    stopTranscoderLiveTask: builder.mutation<void, string>({
      query: id => ({
        url: `manager/stop/${id}`,
        method: 'POST',
      }),
      invalidatesTags: [liveTaskTag],
    }),
    startTranscoderLiveTask: builder.mutation<void, string>({
      query: id => ({
        url: `manager/start/${id}`,
        method: 'POST',
      }),
      invalidatesTags: [liveTaskTag],
    }),
    getTranscoderEncoders: builder.query<TranscoderEncoder[], void>({
      query: () => `manager/encoders`,
    }),
    getTranscoderWorkers: builder.query<TranscoderWorker[], void>({
      query: () => 'workers',
      keepUnusedDataFor: 5,
      transformErrorResponse: () => ({
        code: 200,
        data: [],
      }),
      providesTags: [workerTag],
    }),
  }),
});

export const {
  useGetTranscoderLiveTasksQuery,
  useGetTranscoderLiveTaskQuery,
  useDeleteTranscoderLiveTaskMutation,
  useCreateTranscoderLiveTaskMutation,
  useUpdateTranscoderLiveTaskMutation,
  useDeleteTranscoderLiveTaskHolisticMutation,
  useCreateTranscoderLiveTaskHolisticMutation,
  useUpdateTranscoderLiveTaskHolisticMutation,
  useStopTranscoderLiveTaskMutation,
  useStartTranscoderLiveTaskMutation,
  useGetTranscoderEncodersQuery,
  useGetTranscoderWorkersQuery,
} = transcoderLiveTaskApi;
